import React, {useCallback} from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import SubtemplateStore from 'notebook/entry-templates/SubtemplateStore';
import {TemplateOrSubtemplateType} from 'notebook/entry-templates/TemplateConstants';
import TemplateStore from 'notebook/entry-templates/TemplateStore';
import {VersionBase} from 'notebook/store/VersionedItemSubstore';
import ButtonWithDisabledReason from 'ui-library/buttons/ButtonWithDisabledReason';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

export const EXPORT_TEMPLATE_BUTTON_TEXT = 'Export template';
export const EXPORT_SUBTEMPLATE_BUTTON_TEXT = 'Export sub-template';
export const EXPORTING_PRIOR_VERSIONS_NOT_SUPPORTED = 'Exporting prior versions is not supported';

interface TemplateExportButtonProps {
  isViewingLatestVersion: boolean;
  onClick: () => Promise<void>;
  openVersionApiId: string | undefined;
  templateId: string;
  templateType: TemplateOrSubtemplateType;
}

export default function TemplateExportButton({
  isViewingLatestVersion,
  onClick,
  openVersionApiId,
  templateId,
  templateType,
}: TemplateExportButtonProps): JSX.Element {
  const latestVisibleVersionApiId = useLatestVisibleVersionApiId(templateId, templateType);
  const isExportEnabled = latestVisibleVersionApiId
    ? latestVisibleVersionApiId === openVersionApiId
    : isViewingLatestVersion;

  return (
    <ButtonWithDisabledReason
      disabledTooltip={isExportEnabled ? null : EXPORTING_PRIOR_VERSIONS_NOT_SUPPORTED}
      fullWidth={true}
      onClick={onClick}
      size="sm"
      variant="tertiary"
    >
      {templateType === ModelTypeConstants.SUBTEMPLATE
        ? EXPORT_SUBTEMPLATE_BUTTON_TEXT
        : EXPORT_TEMPLATE_BUTTON_TEXT}
    </ButtonWithDisabledReason>
  );
}

/** The last version of the history the viewer can see, which is the version an export would produce. */
function useLatestVisibleVersionApiId(
  templateId: string,
  templateType: TemplateOrSubtemplateType
): string | undefined {
  const store = templateType === ModelTypeConstants.TEMPLATE ? TemplateStore : SubtemplateStore;
  const selectLatestVisibleVersionApiId = useCallback(
    (versionedStore: typeof store): string | undefined => {
      const versionState = versionedStore.getVersionState(templateId);
      if (versionState == null || !versionState.fetchSuccess) {
        return undefined;
      }
      const {versions} = versionState;
      const latest: VersionBase | undefined = versions[versions.length - 1];
      return latest == null ? undefined : (latest.apiIdentifier ?? latest.api_identifier);
    },
    [templateId]
  );
  return useStoreSelector(store, selectLatestVisibleVersionApiId);
}
